import type { ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronRight, Users } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Section } from "../components/ui/section";
import { PageHeader } from "../components/ui/page-header";
import { Avatar } from "../components/ui/avatar";
import { useMe, useUserProfile } from "../api/hooks";
import { formatRole } from "../lib/groupRole";
import type { SharedGroup } from "../api/types";

/**
 * Another member's profile, reached by tapping their name on a group's People
 * list (issue #452).
 *
 * Deliberately thin. It shows who they are and where you overlap with them, and
 * nothing account-private: no email, no credit balance, no preferences. The
 * server intersects the group list with the caller's own memberships, so this
 * page can't be used to enumerate someone's groups.
 */
export function UserProfilePage(): ReactNode {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const meQ = useMe();
  const { data, isLoading, isError } = useUserProfile(userId);
  const isMe = meQ.data?.id === userId;

  return (
    <div className="mx-auto max-w-xl space-y-stack-lg">
      <PageHeader back={{ label: "Back", onClick: () => navigate(-1) }} />

      {isLoading ? (
        <ProfileSkeleton />
      ) : isError || !data ? (
        <Card tone="ghost">
          <CardContent className="py-12 text-center text-on-surface-variant">
            We couldn&apos;t find that person.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex items-center gap-4">
            <Avatar name={data.user.displayName} size="lg" />
            <div className="min-w-0">
              <h1 className="truncate text-headline-lg">
                {data.user.displayName}
              </h1>
              <p className="text-body-md text-on-surface-variant">
                {isMe
                  ? "This is you"
                  : sharedGroupsSummary(data.sharedGroups.length)}
              </p>
            </div>
          </div>

          <Section
            title={isMe ? "Your groups" : "Groups you're both in"}
            description={
              isMe
                ? undefined
                : "Only groups you belong to are listed here — you can't see the rest of someone's groups."
            }
            contentClassName="space-y-stack-sm"
          >
            {data.sharedGroups.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <span className="grid size-14 place-items-center rounded-full bg-surface-variant text-outline">
                  <Users className="size-6" />
                </span>
                <p className="text-body-md text-on-surface-variant">
                  You aren&apos;t in any groups together.
                </p>
              </div>
            ) : (
              data.sharedGroups.map((group) => (
                <SharedGroupRow key={group.id} group={group} isMe={isMe} />
              ))
            )}
          </Section>

          {/*
            The id is already in the URL on web; repeating it here — muted and
            selectable — is what makes it usable on desktop, which has no
            address bar. Mobile deliberately doesn't show it (issue #452).
          */}
          <p className="text-label-sm text-on-surface-variant">
            <span className="select-all font-mono">{data.user.id}</span>
          </p>
        </>
      )}
    </div>
  );
}

function sharedGroupsSummary(count: number): string {
  if (count === 0) return "No groups in common";
  return count === 1 ? "1 group in common" : `${count} groups in common`;
}

function SharedGroupRow({
  group,
  isMe,
}: {
  group: SharedGroup;
  isMe: boolean;
}): ReactNode {
  return (
    <Link
      to={`/groups/${group.id}`}
      className="flex items-center justify-between gap-4 rounded-md border border-outline-variant/60 p-4 transition-colors hover:border-primary/50 hover:bg-surface-container/40"
    >
      <div className="min-w-0">
        <p className="truncate text-body-lg">{group.label}</p>
        <p className="text-label-md text-on-surface-variant">
          {isMe ? formatRole(group.role) : `They're ${formatRole(group.role)}`}{" "}
          · {group.currency}
        </p>
      </div>
      <ChevronRight className="size-4 shrink-0 text-on-surface-variant" />
    </Link>
  );
}

function ProfileSkeleton(): ReactNode {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="size-14 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <Skeleton className="h-40 rounded-md" />
    </div>
  );
}
